import type { PartnerEdition, PartnerKunde } from '@nac2s/shared'
import wasserzeichen from '@nac2s/ui/marke/veeky-schriftzug.png'
import { SearchLg } from '@untitledui/icons'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { PaginationPageDefault } from '@/components/application/pagination/pagination'
import { Input } from '@/components/base/input/input'
import { Hinweis } from '@/teile/Hinweis'
import { Status } from '@/teile/Status'
import { Zahl } from '@/teile/Zahl'
import { cx } from '@/utils/cx'
import { ApiError } from '../api/client.js'
import { ladeEditionen, ladeKunden } from '../api/partner.js'

/**
 * Kundenliste des Partners.
 *
 * **Verwaltungsdaten, keine Gesprächsdaten** (`Projektstruktur.md` §3). Was
 * hier nicht steht, liefert die API auch nicht.
 *
 * Der Aufbau folgt der Menge: Bei zweihundert Kunden ist die Liste ein
 * Findewerkzeug. Deshalb steht die Suche vorn, „Braucht Sie" bekommt einen
 * eigenen Block, und der Rest kommt seitenweise.
 *
 * **Gefiltert und geblättert wird hier, nicht im Backend.** Das trägt, solange
 * ein Partner zweistellig viele Kunden hat — und der Endpunkt liefert heute
 * ohnehin alle. Ab dreistellig gehört beides auf den Server, zusammen mit
 * fortgeschriebenen Ständen in der Registry statt Zählungen je Aufruf.
 */

const JE_SEITE = 12

type Ladezustand =
  | { status: 'laedt' }
  | { status: 'da'; kunden: PartnerKunde[] }
  | { status: 'fehler'; meldung: string }

type Filter = 'alle' | 'dringend' | 'laeuft' | 'ruht'
type Sortierung = 'name' | 'dringlich' | 'seeds' | 'neu'

/** Was an einem Kunden auffällt — die Zeile soll sagen, was zu tun ist. */
interface Merker {
  text: string
  hart: boolean
}

function merkerVon(kunde: PartnerKunde): Merker[] {
  const merker: Merker[] = []
  if (kunde.zustand === 'bereitstellung') merker.push({ text: 'Auftrag offen', hart: false })
  if (
    kunde.zustand === 'aktiv' &&
    kunde.seatCap !== null &&
    (kunde.seatsBelegt ?? 0) >= kunde.seatCap
  ) {
    merker.push({ text: 'Cap erreicht', hart: true })
  }
  if (kunde.anwenderOhneZugang) {
    merker.push({ text: `${kunde.anwenderOhneZugang} ohne sipgate-Zugang`, hart: false })
  }
  if (kunde.geraeteOhneZeichen) {
    merker.push({ text: `${kunde.geraeteOhneZeichen} Karteileichen`, hart: false })
  }
  return merker
}

const istDringend = (kunde: PartnerKunde) => merkerVon(kunde).length > 0

export function Kunden() {
  const [zustand, setZustand] = useState<Ladezustand>({ status: 'laedt' })
  const [suche, setSuche] = useState('')
  const [filter, setFilter] = useState<Filter>('alle')
  const [sortierung, setSortierung] = useState<Sortierung>('name')
  const [seite, setSeite] = useState(1)
  const [alleDringend, setAlleDringend] = useState(false)
  const [auslaufend, setAuslaufend] = useState<PartnerEdition[]>([])
  const [editionsnamen, setEditionsnamen] = useState<ReadonlyMap<string, string>>(new Map())
  const navigate = useNavigate()

  // Editionen mit Ende, die noch Kunden haben — der Partner soll das vorher
  // sehen, nicht erst, wenn die Umstellung scheitert (ADR-0014).
  useEffect(() => {
    let abgebrochen = false
    ladeEditionen()
      .then((k) => {
        if (abgebrochen) return
        setAuslaufend(k.editionen.filter((e) => e.eolAm !== null && e.kunden > 0))
        setEditionsnamen(new Map(k.editionen.map((e) => [e.key, e.name])))
      })
      .catch(() => {
        // Beiwerk: Ohne Katalog fehlt nur der Hinweis.
      })
    return () => {
      abgebrochen = true
    }
  }, [])

  useEffect(() => {
    let abgebrochen = false
    ladeKunden()
      .then((kunden) => {
        if (!abgebrochen) setZustand({ status: 'da', kunden })
      })
      .catch((fehler: unknown) => {
        if (abgebrochen) return
        setZustand({
          status: 'fehler',
          meldung: fehler instanceof ApiError ? fehler.message : 'Unbekannter Fehler',
        })
      })
    return () => {
      abgebrochen = true
    }
  }, [])

  const alle = zustand.status === 'da' ? zustand.kunden : []
  const dringende = useMemo(() => alle.filter(istDringend), [alle])

  const gefiltert = useMemo(() => {
    const s = suche.trim().toLowerCase()
    const treffer = alle
      .filter((k) => {
        if (filter === 'dringend') return istDringend(k)
        if (filter === 'laeuft') return k.zustand === 'aktiv' && !istDringend(k)
        if (filter === 'ruht') return k.zustand === 'deaktiviert'
        return true
      })
      .filter((k) => !s || k.anzeigename.toLowerCase().includes(s) || k.host.includes(s))

    return [...treffer].sort((a, b) => {
      if (sortierung === 'seeds') return (b.seatsBelegt ?? 0) - (a.seatsBelegt ?? 0)
      if (sortierung === 'neu') return b.angelegtAm.localeCompare(a.angelegtAm)
      if (sortierung === 'dringlich') {
        const d = (istDringend(b) ? 1 : 0) - (istDringend(a) ? 1 : 0)
        if (d !== 0) return d
      }
      return a.anzeigename.localeCompare(b.anzeigename, 'de')
    })
  }, [alle, filter, suche, sortierung])

  if (zustand.status === 'laedt') {
    return (
      <p role="status" className="px-6 py-10 text-sm text-tertiary">
        Kunden werden geladen …
      </p>
    )
  }

  if (zustand.status === 'fehler') {
    return (
      <div className="px-6 py-10">
        <div className="rounded-xl bg-error-primary p-4 text-sm text-error-primary">
          <p className="font-medium">Die Kundenliste konnte nicht geladen werden</p>
          <p className="mt-1">{zustand.meldung}</p>
        </div>
      </div>
    )
  }

  const seiten = Math.max(1, Math.ceil(gefiltert.length / JE_SEITE))
  const aktuelleSeite = Math.min(seite, seiten)
  const von = (aktuelleSeite - 1) * JE_SEITE
  const sichtbar = gefiltert.slice(von, von + JE_SEITE)

  const belegtGesamt = alle.reduce((summe, k) => summe + (k.seatsBelegt ?? 0), 0)
  const gezeigteDringende = alleDringend ? dringende : dringende.slice(0, 3)

  const setzeFilter = (neu: Filter) => {
    setFilter(neu)
    setSeite(1)
  }

  return (
    <div>
      {/* --- Kopfzahl mit Schimmer aus der Ecke -------------------------
          `overflow-hidden` schneidet den Verlauf oben an der Trennlinie zur
          Kopfleiste ab — dort faellt der Schnitt mit einer Kante zusammen,
          die ohnehin da ist. Nach rechts und unten laeuft er vorher aus. */}
      <div className="relative overflow-hidden px-6 pt-6 pb-[18px]">
        <div
          aria-hidden="true"
          className="schimmer pointer-events-none absolute -top-[13rem] -right-[6rem] size-[25rem]"
        />
        {/* Die Marke als Wasserzeichen — **hinter** dem Inhalt, nicht daneben.
            Wird die Zahl oder die Beschriftung breiter, laeuft sie darueber;
            das ist Absicht und der Grund, warum es kein eigener Platz ist.
            Unter 1024 Pixel Breite bleibt es weg: Dort steht der Text ueber
            der ganzen Flaeche, und das Zeichen wuerde ihn nur truebe machen.

            Mittig in beiden Richtungen, und die Breite so gewaehlt, dass die
            Hoehe **in** den Block passt: 20rem ergeben 108 Pixel bei 142 Pixel
            Blockhoehe. Groesser schnitte der `overflow-hidden` oben und unten
            ab, und ein angeschnittenes Logo sieht nach Versehen aus. */}
        <img
          src={wasserzeichen}
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute top-1/2 left-1/2 hidden w-[20rem] -translate-x-1/2 -translate-y-1/2 opacity-[0.08] select-none lg:block"
        />
        <div className="relative">
          <p className="text-[13px] text-quaternary">{heute()}</p>
          {/* 28 Pixel Abstand und 60,8 Pixel Schrift — beides an der Vorlage
              nachgemessen, nicht geschaetzt. */}
          <div className="mt-7 flex flex-wrap items-end gap-6">
            <Zahl
              wert={belegtGesamt}
              className="font-display text-[3.8rem] leading-[0.86] font-extrabold tracking-[-0.035em] text-primary"
            />
            <div className="pb-1">
              <p className="font-semibold text-primary">Seeds belegt</p>
              <p className="text-sm text-tertiary">
                über {alle.length} Kunden · nur Anwender mit sipgate-Zugang
              </p>
            </div>
            <dl className="ml-auto flex flex-wrap gap-6 pb-1">
              <Kennzahl name="Kunden" wert={alle.length} />
              <Kennzahl name="Brauchen Sie" wert={dringende.length} achtung />
            </dl>
          </div>
        </div>
      </div>

      {auslaufend.length > 0 ? (
        <div className="px-6 pb-3">
          <Hinweis titel="Editionen mit Ende">
            {auslaufend.map((e) => (
              <span key={e.key} className="block">
                <b className="font-semibold">{e.name}</b> endet am{' '}
                {new Date(`${e.eolAm}T00:00:00`).toLocaleDateString('de-DE')} —{' '}
                {e.kunden === 1 ? 'ein Kunde hat sie noch' : `${e.kunden} Kunden haben sie noch`}.
                Bestandskunden behalten sie; neu zuweisen lässt sie sich danach nicht mehr.
              </span>
            ))}
          </Hinweis>
        </div>
      ) : null}

      {(() => {
        const bald = alle.filter(
          (k) =>
            k.lizenz?.laufzeit === 'mindest' &&
            new Date(`${k.lizenz.laufzeitEnde}T00:00:00`).getTime() - Date.now() < 31 * 86_400_000,
        )
        return bald.length > 0 ? (
          <div className="px-6 pb-3">
            <Hinweis titel="Lizenzen, die sich bald verlängern">
              {bald.map((k) => (
                <span key={k.host} className="block">
                  <b className="font-semibold">{k.anzeigename}</b>: Die Mindestlaufzeit endet am{' '}
                  {new Date(`${k.lizenz?.laufzeitEnde}T00:00:00`).toLocaleDateString('de-DE')} und
                  verlängert sich dann um zwölf Monate. Bis dahin lässt sich die Edition wechseln
                  oder auf monatliche Laufzeit umstellen.
                </span>
              ))}
            </Hinweis>
          </div>
        ) : null
      })()}

      {/* --- Suche, Filter, Sortierung ---------------------------------- */}
      <div className="flex flex-col gap-2.5 px-6 pt-1.5 pb-3.5">
        {/* Das Kuerzel steht **neben** dem Bauteil, nicht darin.
            Untitled UI legt hinter seinen `shortcut` einen weissen Verlauf,
            damit langer Text ausblendet — auf grauem Grund sieht man den
            als hellen Fleck. Die Vorlage hat dort nur ein Schildchen. */}
        <div className="relative">
          <Input
            size="md"
            type="search"
            icon={SearchLg}
            aria-label="Kunde oder Adresse suchen"
            placeholder="Kunde oder Adresse suchen …"
            // Masse aus der Vorlage: Hoehe 47, Radius 14, Polster 10/14,
            // Lupe 16, Schrift 14,5, grau hinterlegt, ohne Schatten.
            //
            // Das Polster gehoert an das Eingabefeld, nicht an den Rahmen:
            // Untitled UI setzt es im Feld, und ein zweites am Rahmen legt
            // sich obendrauf — genau daran war die Zeile zu hoch.
            //
            // Links 40 Pixel Platz: 14 Rand + 16 Lupe + 10 Abstand.
            // Rechts 56, damit der Text nicht unter das Kuerzel laeuft.
            wrapperClassName="feld min-h-[47px] rounded-[14px] bg-secondary ring-0 transition-[box-shadow,background-color] duration-[180ms] ease-out focus-within:feld-schein focus-within:bg-primary"
            inputClassName="py-2.5 pr-14 pl-10 text-[14.5px]"
            iconClassName="left-3.5 size-4"
            value={suche}
            onChange={(wert: string) => {
              setSuche(wert)
              setSeite(1)
            }}
          />
          <span
            aria-hidden="true"
            className="pointer-events-none absolute top-1/2 right-3.5 -translate-y-1/2 rounded-md border border-secondary bg-primary px-1.5 py-px text-[11px] text-quaternary select-none"
          >
            ⌘K
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Pille an={filter === 'alle'} onWahl={() => setzeFilter('alle')} zahl={alle.length}>
            Alle
          </Pille>
          <Pille
            an={filter === 'dringend'}
            onWahl={() => setzeFilter('dringend')}
            zahl={dringende.length}
          >
            Braucht Sie
          </Pille>
          <Pille
            an={filter === 'laeuft'}
            onWahl={() => setzeFilter('laeuft')}
            zahl={alle.filter((k) => k.zustand === 'aktiv' && !istDringend(k)).length}
          >
            Läuft
          </Pille>
          <Pille
            an={filter === 'ruht'}
            onWahl={() => setzeFilter('ruht')}
            zahl={alle.filter((k) => k.zustand === 'deaktiviert').length}
          >
            Ruht
          </Pille>

          <label className="ml-auto flex items-center gap-2 text-sm text-quaternary">
            Sortiert nach
            <select
              value={sortierung}
              onChange={(e) => {
                setSortierung(e.target.value as Sortierung)
                setSeite(1)
              }}
              className="cursor-pointer rounded-full border border-secondary bg-primary px-3 py-1.5 text-sm text-tertiary"
            >
              <option value="name">Name</option>
              <option value="dringlich">Dringlichkeit</option>
              <option value="seeds">Seeds</option>
              <option value="neu">Zuletzt angelegt</option>
            </select>
          </label>
        </div>
      </div>

      {/* --- Braucht Sie ------------------------------------------------ */}
      {filter === 'alle' && dringende.length > 0 ? (
        <section className="px-6 pb-2">
          <div className="flex items-baseline gap-2 py-2">
            <h2 className="font-display text-base font-bold text-primary">Braucht Sie</h2>
            <span className="text-sm text-tertiary">
              {gezeigteDringende.length} von {dringende.length}
            </span>
            {dringende.length > 3 ? (
              <button
                type="button"
                className="ml-auto text-sm font-medium text-akzent-tief hover:underline"
                onClick={() => setAlleDringend((v) => !v)}
              >
                {alleDringend ? 'Weniger anzeigen' : 'Alle anzeigen'}
              </button>
            ) : null}
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {gezeigteDringende.map((kunde) => (
              <DringendKarte
                key={kunde.id}
                kunde={kunde}
                onOeffnen={() => navigate(`/kunden/${kunde.host}`)}
              />
            ))}
          </div>
        </section>
      ) : null}

      {/* --- Liste ------------------------------------------------------ */}
      <section className="px-6 pb-2">
        <div className="flex items-baseline gap-2 py-2">
          <h2 className="font-display text-base font-bold text-primary">
            {suche.trim() ? `Treffer für „${suche.trim()}"` : titelVon(filter)}
          </h2>
          <span className="text-sm text-tertiary">
            {gefiltert.length} {gefiltert.length === 1 ? 'Kunde' : 'Kunden'}
          </span>
        </div>

        {sichtbar.length === 0 ? (
          <div className="rounded-xl border border-dashed border-secondary p-10 text-center">
            <p className="font-medium text-primary">Kein Kunde gefunden</p>
            <p className="mt-1 text-sm text-tertiary">
              Andere Schreibweise versuchen oder den Filter zurücksetzen.
            </p>
          </div>
        ) : (
          <div className="px-[14px]">
            {/* Kein `<table>`: Ein `<tr>` nimmt keine Rundung an, der Hover
                bekaeme Ecken; und eine Mindestbreite erzwingt einen
                Rollbalken, sobald der Pfeil beim Zeigen wandert. Masse aus
                der Vorlage: Spalten 30 / 9rem / 8,5rem / 7rem / 22, Abstand
                14, Polster 9/12, Radius 12. */}
            <div
              aria-hidden="true"
              className="grid grid-cols-[30px_minmax(9rem,1.5fr)_6.5rem_8.5rem_minmax(7rem,1fr)_22px] gap-[14px] border-b border-secondary px-3 py-2 text-[11.5px] text-quaternary"
            >
              <span />
              <span>Kunde</span>
              <span>Edition</span>
              <span>Seeds</span>
              <span>Hinweis</span>
              <span />
            </div>

            <ul>
              {sichtbar.map((kunde) => (
                <li key={kunde.id}>
                  <button
                    type="button"
                    onClick={() => navigate(`/kunden/${kunde.host}`)}
                    className="group grid w-full cursor-pointer grid-cols-[30px_minmax(9rem,1.5fr)_6.5rem_8.5rem_minmax(7rem,1fr)_22px] gap-[14px] items-center rounded-[12px] border-b border-secondary px-3 py-[9px] text-left transition-colors last:border-b-0 hover:bg-secondary focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-akzent"
                  >
                    <span
                      aria-hidden="true"
                      className={cx(
                        'grid size-[30px] place-items-center rounded-[10px] font-display text-xs font-bold tracking-tight',
                        zeichenFarbe(kunde),
                      )}
                    >
                      {kuerzel(kunde.anzeigename)}
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-semibold text-primary">
                        {kunde.anzeigename}
                      </span>
                      <span className="block truncate text-[11.5px] text-quaternary">
                        {kunde.host}
                      </span>
                    </span>
                    <span className="truncate text-[12.5px] text-tertiary">
                      {editionsnamen.get(kunde.edition) ?? kunde.edition}
                    </span>
                    <SeedsZelle kunde={kunde} />
                    <Hinweise kunde={kunde} />
                    {/* Ein Winkel mit 13 Pixeln und Strichstaerke 1,8 — wie
                        in der Vorlage. Das Zeichen „›" haengt an der
                        Schriftart und wird darin winzig. */}
                    <svg
                      viewBox="0 0 16 16"
                      fill="none"
                      aria-hidden="true"
                      className="size-[13px] text-quaternary transition-transform group-hover:translate-x-0.5"
                    >
                      <path
                        d="M6 3l5 5-5 5"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {seiten > 1 ? (
        <div className="flex flex-wrap items-center gap-3 border-t border-secondary px-6 py-4">
          <span className="text-sm text-tertiary tabular-nums">
            {von + 1}–{von + sichtbar.length} von {gefiltert.length}
          </span>
          <PaginationPageDefault
            className="ml-auto"
            page={aktuelleSeite}
            total={seiten}
            onPageChange={setSeite}
          />
        </div>
      ) : (
        <div className="pb-6" />
      )}
    </div>
  )
}

function titelVon(filter: Filter): string {
  if (filter === 'dringend') return 'Braucht Sie'
  if (filter === 'laeuft') return 'Läuft ohne Zutun'
  if (filter === 'ruht') return 'Ruht'
  return 'Alle Kunden'
}

/** „Donnerstag, 10. September" — der Tag, an dem jemand hier sitzt. */
function heute(): string {
  return new Date().toLocaleDateString('de-DE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  })
}

function zeichenFarbe(kunde: PartnerKunde): string {
  if (kunde.zustand === 'deaktiviert') return 'bg-secondary text-tertiary'
  const merker = merkerVon(kunde)
  if (merker.some((m) => m.hart)) return 'bg-error-primary text-fg-error-primary'
  if (merker.length > 0) return 'bg-achtung-flaeche text-achtung-tief'
  return 'bg-success-primary text-fg-success-primary'
}

function Kennzahl({ name, wert, achtung }: { name: string; wert: number; achtung?: boolean }) {
  return (
    <div>
      <dt className="text-xs text-tertiary">{name}</dt>
      <dd
        className={cx(
          'font-display text-xl font-bold',
          achtung ? 'text-achtung-tief' : 'text-primary',
        )}
      >
        <Zahl wert={wert} />
      </dd>
    </div>
  )
}

function Pille({
  children,
  zahl,
  an,
  onWahl,
}: {
  children: string
  zahl: number
  an: boolean
  onWahl: () => void
}) {
  return (
    // Masse aus der Vorlage: vollrund, 13 Pixel Schrift, Polster 6/14,
    // ein Haarstrich als Rand. Untitled UI legt auf seine Schaltflaechen
    // Ring und Schatten — die wirken erhaben, und vier davon nebeneinander
    // sehen aus wie eine Werkzeugleiste statt wie ein Filter.
    //
    // Beim Zeigen hebt sich die Pille um einen Pixel, Rand und Schrift
    // werden dunkler — der Grund bleibt. So steht es in der Vorlage.
    <button
      type="button"
      onClick={onWahl}
      aria-pressed={an}
      className={cx(
        'cursor-pointer rounded-full border px-3.5 py-1.5 text-[13px] transition-[transform,border-color,color,background-color] duration-150',
        an
          ? 'border-transparent bg-akzent font-semibold text-white'
          : 'border-secondary bg-primary font-medium text-tertiary hover:-translate-y-px hover:border-rand-aktiv hover:text-primary',
      )}
    >
      {children}
      <span className="ml-[5px] opacity-60 tabular-nums">{zahl}</span>
    </button>
  )
}

/**
 * Seeds in der Zeile.
 *
 * „unbekannt" statt einer Null, wo die Kundendatenbank nicht antwortet: Eine
 * Null sähe aus wie ein leerer Kunde, und darauf würde jemand über Seeds
 * entscheiden.
 */
function SeedsZelle({ kunde }: { kunde: PartnerKunde }) {
  if (kunde.zustand !== 'aktiv') {
    return (
      <span className="text-sm text-tertiary">
        {kunde.zustand === 'bereitstellung' ? 'wird bereitgestellt' : 'deaktiviert'}
      </span>
    )
  }
  if (kunde.seatsBelegt === null) {
    return <span className="text-sm text-tertiary">unbekannt</span>
  }
  return (
    <span className="text-[12.5px] text-tertiary tabular-nums">
      <b className="font-semibold text-primary">{kunde.seatsBelegt}</b> /{' '}
      {kunde.seatCap === null ? '∞' : kunde.seatCap}
    </span>
  )
}

/**
 * Hinweise als Schilder.
 *
 * Dieselbe Darstellung wie jeder andere Zustand (`teile/Status.tsx`). Vorher
 * standen hier eigene Masse — eine Spur kleiner als das Schild am
 * Geraetezustand, und dadurch las sich dieselbe Aussage auf zwei Seiten
 * verschieden.
 */
function Hinweise({ kunde }: { kunde: PartnerKunde }) {
  const merker = merkerVon(kunde)

  if (merker.length === 0) {
    return kunde.zustand === 'aktiv' ? (
      <Status ton="gut">läuft</Status>
    ) : (
      <Status ton="ruhig">ruht</Status>
    )
  }

  return (
    <span className="flex w-fit flex-wrap gap-1.5">
      {merker.map((m) => (
        <Status key={m.text} ton={m.hart ? 'stoerung' : 'wartet'}>
          {m.text}
        </Status>
      ))}
    </span>
  )
}

function DringendKarte({ kunde, onOeffnen }: { kunde: PartnerKunde; onOeffnen: () => void }) {
  const merker = merkerVon(kunde)
  const hart = merker.some((m) => m.hart)
  const tat = hart
    ? 'Seeds erhöhen'
    : kunde.zustand === 'bereitstellung'
      ? 'Auftrag freigeben'
      : 'Kopplungscodes ausgeben'

  return (
    <button
      type="button"
      onClick={onOeffnen}
      className={cx(
        'flex flex-col gap-2 rounded-xl border border-secondary bg-primary p-3.5 text-left',
        'border-l-[3px] transition hover:-translate-y-0.5 hover:shadow-md',
        hart ? 'border-l-fg-error-primary' : 'border-l-achtung',
      )}
    >
      <span className="font-semibold text-primary">{kunde.anzeigename}</span>
      <span className="text-sm text-tertiary">{merker.map((m) => m.text).join(' · ')}</span>
      <span
        className={cx(
          'text-sm font-semibold',
          hart ? 'text-fg-error-primary' : 'text-achtung-tief',
        )}
      >
        {tat} →
      </span>
    </button>
  )
}

/** Höchstens zwei Buchstaben — drei werden im Feld zu klein. */
function kuerzel(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((teil) => teil[0])
    .join('')
    .toUpperCase()
}
